import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface ContentEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  siteId: string;
  contentType: 'posts' | 'pages';
  item: {
    id: string;
    wp_id: number;
    title: string;
    metadata: {
      content?: { raw?: string; rendered?: string };
      [key: string]: unknown;
    };
  };
  onSuccess?: () => void;
}

type EditorMode = 'classic' | 'blocks' | 'page-builder';

interface PageBuilderInfo {
  name: string;
  detected: boolean;
}

/**
 * Detect the content type and appropriate editor mode
 */
function detectContentType(content: string | undefined): {
  mode: EditorMode;
  pageBuilder: PageBuilderInfo | null;
} {
  if (!content) {
    return { mode: 'classic', pageBuilder: null };
  }

  // Check for page builder shortcodes
  const pageBuilders: Array<{ pattern: RegExp; name: string }> = [
    { pattern: /\[elementor/i, name: 'Elementor' },
    { pattern: /\[et_pb_/i, name: 'Divi Builder' },
    { pattern: /\[vc_/i, name: 'WPBakery Page Builder' },
    { pattern: /\[fusion_/i, name: 'Avada Builder' },
    { pattern: /\[fl_builder/i, name: 'Beaver Builder' },
    { pattern: /\[oxygen/i, name: 'Oxygen Builder' },
    { pattern: /\[bricks_/i, name: 'Bricks Builder' },
  ];

  for (const builder of pageBuilders) {
    if (builder.pattern.test(content)) {
      return {
        mode: 'page-builder',
        pageBuilder: { name: builder.name, detected: true },
      };
    }
  }

  // Check for Gutenberg blocks
  if (content.includes('<!-- wp:')) {
    return { mode: 'blocks', pageBuilder: null };
  }

  // Default to classic editor
  return { mode: 'classic', pageBuilder: null };
}

/**
 * Get mode display info
 */
function getModeInfo(mode: EditorMode): { label: string; color: string; icon: string } {
  switch (mode) {
    case 'blocks':
      return {
        label: 'Block Editor',
        color: 'bg-purple-100 text-purple-700 border-purple-200',
        icon: 'M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z',
      };
    case 'page-builder':
      return {
        label: 'Page Builder',
        color: 'bg-amber-100 text-amber-700 border-amber-200',
        icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
      };
    default:
      return {
        label: 'Classic Editor',
        color: 'bg-blue-100 text-blue-700 border-blue-200',
        icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
      };
  }
}

export function ContentEditorModal({
  isOpen,
  onClose,
  siteId,
  contentType,
  item,
  onSuccess,
}: ContentEditorModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const queryClient = useQueryClient();

  // Get raw content (prefer raw, fallback to rendered)
  const initialContent = item.metadata?.content?.raw ?? item.metadata?.content?.rendered ?? '';
  const renderedContent = item.metadata?.content?.rendered ?? '';

  // State
  const [content, setContent] = useState(initialContent);
  const [showPreview, setShowPreview] = useState(true);
  const [advancedMode, setAdvancedMode] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Detect content mode
  const { mode, pageBuilder } = useMemo(
    () => detectContentType(initialContent),
    [initialContent]
  );

  const modeInfo = getModeInfo(mode);
  const isEditable = mode !== 'page-builder';

  // Reset state when modal opens with new item
  useEffect(() => {
    if (isOpen) {
      const rawContent = item.metadata?.content?.raw ?? item.metadata?.content?.rendered ?? '';
      setContent(rawContent);
      setHasChanges(false);
      setAdvancedMode(false);
    }
  }, [isOpen, item]);

  // Track changes
  useEffect(() => {
    setHasChanges(content !== initialContent);
  }, [content, initialContent]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, hasChanges]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (newContent: string) => {
      // Call the WordPress REST API through our backend to update content
      return api.post(`/api/v1/sites/${siteId}/${contentType}/${item.wp_id}/edit`, {
        fields: { content: newContent },
      });
    },
    onSuccess: () => {
      // Invalidate queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['site-content', siteId] });
      onSuccess?.();
      onClose();
    },
  });

  const handleClose = useCallback(() => {
    if (hasChanges) {
      if (!confirm('You have unsaved changes. Are you sure you want to close?')) {
        return;
      }
    }
    onClose();
  }, [hasChanges, onClose]);

  const handleSave = useCallback(() => {
    saveMutation.mutate(content);
  }, [content, saveMutation]);

  // Build WordPress edit URL
  const wpEditUrl = useMemo(() => {
    // Try to extract the site URL from the item metadata or use a fallback
    const siteUrl = (item.metadata?.link as string)?.split('/').slice(0, 3).join('/') ?? '';
    if (!siteUrl) return null;
    return `${siteUrl}/wp-admin/post.php?post=${item.wp_id}&action=edit`;
  }, [item]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      aria-modal="true"
      role="dialog"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className={cn(
          'relative w-full max-w-7xl h-[90vh]',
          'bg-white rounded-xl shadow-2xl',
          'flex flex-col overflow-hidden',
          'animate-in fade-in-0 zoom-in-95 duration-200'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-4">
            {/* Content icon */}
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg
                className="w-5 h-5 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Edit Content: {item.title}
              </h2>
              <p className="text-sm text-gray-500">
                {contentType === 'posts' ? 'Post' : 'Page'} ID: {item.wp_id}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Mode indicator */}
            <div className={cn(
              'flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium',
              modeInfo.color
            )}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={modeInfo.icon} />
              </svg>
              {modeInfo.label}
            </div>

            {/* Close button */}
            <button
              onClick={handleClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 flex overflow-hidden">
          {mode === 'page-builder' ? (
            /* Page Builder Mode - Read Only */
            <div className="flex-1 flex flex-col">
              {/* Warning Banner */}
              <div className="bg-amber-50 border-b border-amber-200 px-6 py-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-amber-800">
                      This content uses {pageBuilder?.name}
                    </h3>
                    <p className="mt-1 text-sm text-amber-700">
                      Editing content created with {pageBuilder?.name} outside of WordPress may break formatting and design elements.
                      Please edit this content in WordPress to preserve its layout and styling.
                    </p>
                    {wpEditUrl && (
                      <a
                        href={wpEditUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Open in WordPress
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Preview Only */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="max-w-4xl mx-auto">
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
                    Rendered Preview
                  </h3>
                  <div
                    className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-blue-600 prose-img:rounded-lg bg-white border border-gray-200 rounded-lg p-6"
                    dangerouslySetInnerHTML={{ __html: renderedContent }}
                  />
                </div>
              </div>
            </div>
          ) : (
            /* Classic or Block Editor Mode */
            <>
              {/* Editor Panel */}
              <div className={cn(
                'flex flex-col border-r border-gray-200',
                showPreview ? 'w-1/2' : 'flex-1'
              )}>
                {/* Block Editor Warning */}
                {mode === 'blocks' && (
                  <div className={cn(
                    'px-4 py-3 border-b',
                    advancedMode
                      ? 'bg-red-50 border-red-200'
                      : 'bg-purple-50 border-purple-200'
                  )}>
                    {advancedMode ? (
                      <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-red-800">
                            Advanced Mode Enabled
                          </p>
                          <p className="text-sm text-red-700 mt-0.5">
                            You can now edit block structure. Modifying or removing block comments may cause display issues in WordPress.
                          </p>
                        </div>
                        <button
                          onClick={() => setAdvancedMode(false)}
                          className="text-sm text-red-700 hover:text-red-900 underline flex-shrink-0"
                        >
                          Disable
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-start gap-3">
                          <svg className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <p className="text-sm font-medium text-purple-800">
                              Safe Mode - Block content detected
                            </p>
                            <p className="text-sm text-purple-700 mt-0.5">
                              Edit text content within blocks carefully. Structural changes may break the block layout.
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setAdvancedMode(true)}
                          className="text-sm text-purple-700 hover:text-purple-900 underline flex-shrink-0 ml-4"
                        >
                          Enable Advanced Mode
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Editor Header */}
                <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-700">
                    {mode === 'blocks' ? 'Raw Block Content' : 'HTML Content'}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      {content.length.toLocaleString()} characters
                    </span>
                  </div>
                </div>

                {/* Textarea */}
                <div className="flex-1 overflow-hidden">
                  <textarea
                    ref={textareaRef}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    className={cn(
                      'w-full h-full p-4 font-mono text-sm resize-none focus:outline-none',
                      'bg-gray-900 text-gray-100',
                      mode === 'blocks' && !advancedMode && 'bg-gray-800'
                    )}
                    placeholder="Enter content..."
                    spellCheck={false}
                    disabled={mode === 'blocks' && !advancedMode && false} // Still allow editing in safe mode
                  />
                </div>
              </div>

              {/* Preview Panel */}
              {showPreview && (
                <div className="w-1/2 flex flex-col">
                  {/* Preview Header */}
                  <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b border-gray-200">
                    <span className="text-sm font-medium text-gray-700">Preview</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowPreview(false)}
                        className="p-1 text-gray-500 hover:text-gray-700 rounded"
                        title="Hide preview"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Preview Content */}
                  <div className="flex-1 overflow-y-auto p-6 bg-white">
                    <div
                      className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-blue-600 prose-img:rounded-lg"
                      dangerouslySetInnerHTML={{ __html: content }}
                    />
                  </div>
                </div>
              )}

              {/* Show Preview Toggle (when hidden) */}
              {!showPreview && (
                <button
                  onClick={() => setShowPreview(true)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
                  title="Show preview"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">
              Press <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs font-mono">Esc</kbd> to close
            </span>
            {hasChanges && (
              <span className="text-sm text-amber-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Unsaved changes
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Open in WordPress */}
            {wpEditUrl && (
              <a
                href={wpEditUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Open in WordPress
              </a>
            )}

            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>

            {isEditable && (
              <button
                onClick={handleSave}
                disabled={saveMutation.isPending || !hasChanges}
                className={cn(
                  'px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2',
                  hasChanges
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                )}
              >
                {saveMutation.isPending ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Save Changes
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Error Message */}
        {saveMutation.isError && (
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg max-w-md">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="font-medium">Failed to save</p>
                <p className="text-sm mt-1">
                  {saveMutation.error instanceof Error
                    ? saveMutation.error.message
                    : 'An unexpected error occurred'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ContentEditorModal;
